import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import sumBy from 'lodash/sumBy'

import { dateToString } from 'modules/core/lib/dates'

import { BreakdownWorkedTime } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/BankOfHoursPanel/lib/breakdownWorkedTime'
import { calculateWorkedTime } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Worked/lib/calculateWorkedTime'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import BalanceUtils from 'modules/attendance/lib/utils/Balance'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import Accordion from './Accordion'
import EffectiveTime from './EffectiveTime'
import Overview from './Overview'
import SpecialHours from './SpecialHours'
import TimeTracked from './TimeTracked'
import WorkingLocation from './WorkingLocation'

interface Props {
  timeSheetData: ReturnType<typeof useTimesheetData>
  store: Store
}

const TimeBreakdown = ({ store, timeSheetData }: Props) => {
  const {
    aggregatedWorkedTime: workedTime,
    estimatedTimes,
    breakConfigurationsByIds,
    balances,
    featureByName,
  } = timeSheetData

  const workedTimeData = calculateWorkedTime({
    store,
    workedTime,
    estimatedTimes,
  })
  const { adjustedDailyMinutes } = useContractsVersion()

  const { stringDateFrom: startOn, dateToOrToday } = useAttendanceDateNavigator()
  const endOn = dateToString(dateToOrToday, 'backend')
  const { balance } = BalanceUtils.sumDailyBalance(balances, startOn, endOn, !!adjustedDailyMinutes)

  const workedTimeBreakdown = new BreakdownWorkedTime({
    workedTimeBlocks: workedTime?.workedTimeBlocks ?? [],
    workedMinutes: workedTime?.minutes ?? 0,
    breakConfigurationsByIds,
    extraHourMinutes: Math.max(balance, 0),
  })

  const { specialHoursContributions } = workedTimeBreakdown

  const { employeesSettings } = timeSheetData
  const hasLocationEnabled = EmployeesSettingsUtils.hasLocationEnabled(employeesSettings)
  const contractHoursBaselineEnabled =
    EmployeesSettingsUtils.hasContractHoursBaseline(employeesSettings)

  const specialHoursMinutes = sumBy(
    specialHoursContributions,
    (contribution) => contribution.minutes
  )

  const hasCorrectiveBreaks = TimesheetFeatures.CORRECTIVE_BREAKS in featureByName

  return (
    <Box gap="s16" padding="s24">
      <Overview workedTimeData={workedTimeData} workedTimeBreakdown={workedTimeBreakdown} />
      <Accordion workedTimeBreakdown={workedTimeBreakdown} />
      {contractHoursBaselineEnabled && <EffectiveTime workedTimeData={workedTimeData} />}
      <TimeTracked workedTimeData={workedTimeData} hasCorrectiveBreaks={hasCorrectiveBreaks} />
      {specialHoursMinutes > 0 && (
        <SpecialHours
          specialHoursMinutes={specialHoursMinutes}
          specialHoursContributions={specialHoursContributions}
        />
      )}
      {hasLocationEnabled && <WorkingLocation timeSheetData={timeSheetData} />}
    </Box>
  )
}

export default TimeBreakdown
